<template>
  <div v-if="columns.length">
  <list-layout
    no-counts
    no-search
    no-apply-btn
    no-heading
  >
    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
            id="closeout-snapshot-review-filters"
            ref="reviewFilters"
            :filters="filters"
            :apply-filters="applyFilters"
            :reset-filters="resetFilters"
            :filters-pending="filtersPending"
            :filters-applied="filtersApplied"
            @sorted="onSorted"
            @filter="onFilter"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
        :columns="columns"
        :options="options"
        :limit="limit"
        :selectable="true"
        :selected-data="selectedRows"
        selectable-key="snapshotId"
        ref="reviewTable"
        id="closeout-review-table"
        @loaded="onLoaded"
        @sorted="onSorted"
        @pagination="onPagination"
        @loading="onLoading"
      >
        <template v-slot:counts>
          <div class="d-flex align-items-center">
            <pagination
              class="smaller p-0"
              :page="page"
              :count="count"
              :loading="loading"
              v-model="limit"
              @limit="onLimit"
            />
            <t-table-selection-summary
                v-if="selectedRows.length"
                class="ml-2 smaller"
                :selectedObjects="selectedRows.length"
                @clear="clearSelection"
              />
          </div>
        </template>

        <template v-slot:recordingProprietaryId="{ row }">
          <database-link :type="linkTypes.RECORDING" :item-id="row.soundRecordingId">
            {{ row.recordingProprietaryId }}
          </database-link>
        </template>

        <template v-slot:rightsHolderProprietaryId="{ row }">
          <database-link :type="linkTypes.RIGHTS_HOLDER" :item-id="row.rightsHolderId">
            {{ row.rightsHolderProprietaryId }}
          </database-link>
        </template>

        <template v-slot:share="{ row }">
          {{ row.share }}%
        </template>

        <template v-slot:paidAmount="{ row }">
          ${{ row.paidAmount | formatNumber(2, 2) }}
        </template>

        <template v-slot:grossAmount="{ row }">
          ${{ row.grossAmount | formatNumber(2, 2) }}
        </template>

        <template v-slot:preview>
          <fa-icon
            v-if="preview"
            role="button"
            size="1x"
            :icon="['fa', 'check-circle']"
            color="green"
          />
          <fa-icon
            v-else
            role="button"
            size="1x"
            :icon="['fas', 'times']"
            class="text-red"
          />
        </template>

        <template v-slot:payeeNameCloseout="{ row }">
          <div class="d-flex align-items-center">
            <fa-icon
              role="button"
              size="sm"
              :icon="['fa', 'pen']"
              class="clickable"
              @click="openRepresentationModal(row)"
            />
            <span class="ml-2" :style="{ fontWeight: pendingRepresentationMap[row.snapshotId] ? 'bold' : 'normal' }">{{ getPrimaryRepresentative(row).name }}</span>
            <fa-icon
              v-if="getRepresentationSource(row).length > 1"
              class="blue clickable ml-2 more-values-icon"
              size="sm"
              :icon="['fas', 'plus-circle']"
              @click="openRepresentationModal(row)"
            />
          </div>
        </template>

        <template v-slot:societyNameCloseout="{ row }">
          <div class="d-flex align-items-center">
            <fa-icon
              role="button"
              size="sm"
              :icon="['fa', 'pen']"
              class="clickable"
              @click="openRepresentationModal(row)"
            />
            <span class="ml-2" :style="{ fontWeight: pendingRepresentationMap[row.snapshotId] ? 'bold' : 'normal' }">{{ getPrimaryRepresentative(row).societyName }}</span>
            <fa-icon
              v-if="getRepresentationSource(row).length > 1"
              class="blue clickable ml-2 more-values-icon"
              size="sm"
              :icon="['fas', 'plus-circle']"
              @click="openRepresentationModal(row)"
            />
          </div>
        </template>

        <template v-slot:performerCategory="{ row }">
          {{ getPerformerCategoryLabel(row.performerCategory) }}
        </template>

        <template v-slot:ownershipConflict="{ row }">
          <template v-if="row.ownershipConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:lineupConflict="{ row }">
          <template v-if="row.lineupConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:mandateConflict="{ row }">
          <template v-if="row.mandateConflict">
            <fa-icon size="sm" :icon="['fas', 'times']" class="text-red" />
          </template>
          <template v-else>
            <fa-icon size="sm" :icon="['fas', 'check']" class="text-green" />
          </template>
        </template>

        <template v-slot:status="{ row }">
          <b-form-select
            :value="pendingStatusMap[row.snapshotId] !== undefined ? pendingStatusMap[row.snapshotId] : row.status"
            :options="getRowStatusOptions(row.status)"
            :style="{ width: 'auto', minWidth: '110px', fontWeight: pendingStatusMap[row.snapshotId] !== undefined ? 'bold' : 'normal' }"
            :disabled="transactionsCreated"
            @change="(status) => onRowActionChange(row, status)"
          />
        </template>

        <template v-slot:comment="{ row }">
          <div class="d-flex align-items-center">
            <fa-icon
              role="button"
              size="sm"
              :icon="['fa', 'pen']"
              @click="openCommentModal(row)"
              :disabled="transactionsCreated"
            />
            <span class="ml-2" :style="{ fontWeight: pendingCommentMap[row.snapshotId] !== undefined ? 'bold' : 'normal' }">
              {{ pendingCommentMap[row.snapshotId] !== undefined ? pendingCommentMap[row.snapshotId] : row.comment }}
            </span>
          </div>
        </template>
      </t-table>
    </template>
  </list-layout>

  <modal
    id="edit-comment-modal"
    :cancel-title="$t('cancel') | capitalize"
    :ok-title="$t('confirm') | capitalize"
    @ok="confirmCommentEdit()"
    @cancel="$bvModal.hide('edit-comment-modal')"
    modal-type="info"
    centered
    :custom-title="$t('comment') | capitalize"
  >
    <b-form-textarea
      v-model="editingCommentValue"
      rows="3"
      :disabled="transactionsCreated"
    />

    <template v-slot:footer>
      <b-button class="btn-outline-dark" @click="cancelCommentEdit()">
        {{ $t("cancel") | capitalize }}
      </b-button>
      <b-button
        class="btn-outline-red"
        :disabled="transactionsCreated"
        @click="okCommentEdit()"
      >
        {{ $t("confirm") | capitalize }}
      </b-button>
    </template>
  </modal>

  <representation-modal 
    ref="representationModal"
    :items="representationModalItems"
    :college="college"
    :disabled="transactionsCreated"
    @confirm="onRepresentationConfirm"
  />
  </div>
</template>

<script>
import { capitalize, get, snakeCase } from "lodash"
import { closeoutSnapshotStatusActionsLabels, closeoutSnapshotStatusLabels, distributionCollegeTypes, filterTypes, linkTypes, perPageItemsLg, performerCategoryTypes } from "@/constants"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder"
import Pagination from "@/components/Pagination"
import RepresentationModal from "./RepresentationModal.vue"
import TTableSelectionSummary from "@/components/TTableSelectionSummary.vue"
import { listRouteMixin } from "@/utils/mixins"
import { mapState } from "vuex"

export default {
  name: "CloseoutSnapshotReviewTable",
  mixins: [listRouteMixin],
  components: {
    DatabaseLink,
    FilterBuilder,
    Pagination,
    RepresentationModal,
    TTableSelectionSummary,
  },
  props: {
    closeoutId: {
      type: String,
      required: true,
    },
    college: {
      type: String,
      default: null,
    },
    preview: {
      type: Boolean,
      default: true,
    },
    transactionsCreated: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      linkTypes,
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      loading: false,
      selectedRows: [],
      pendingChanges: [],
      editingCommentRow: null,
      editingCommentValue: "",
      representationModalItems: [],
      filters: {
        recordingProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("recording")),
          placeholder: capitalize(this.$t("search_recording")),
          excludeName: "id",
          valueField: "resoundId",
          textFunction: (r => `${r.artist} - ${r.title}`),
          searchName: "fuzzy_search",
          listEndpoint: this.$api.repertoire.recordingsList,
          noWatch: true,
        },
        artist: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("artist")),
        },
        eligibility: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("eligibility")),
          options: [
            { text: capitalize(this.$t("all")), value: null },
            { text: "Eligible", value: "eligible" },
            { text: "Ineligible", value: "ineligible" },
            { text: "Unknown", value: "unknown" },
          ],
        },
        grossAmount: {
          value: "",
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("gross_amount")),
        },
        share: {
          value: "",
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("percent_paid")),
        },
        paidPlaycount: {
          value: "",
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("usage_paid")),
        },
        paidAmount: {
          value: "",
          defaultValue: null,
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
          label: capitalize(this.$t("closeout_gross")),
        },
        lastUpdatedBy: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("last_updated_by")),
        },
        title: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("title")),
        },
        rightsHolderProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
        name: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("rights_holder_name")),
        },
        payeeName: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("paid_payee")),
        },
        payeeNameCloseout: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("current_payee")),
        },
        societyName: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("paid_society")),
          emptyLabel: capitalize(this.$t("all")),
          options: [],
        },
        societyNameCloseout: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("current_society")),
          emptyLabel: capitalize(this.$t("all")),
          options: [],
        },
        status: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("status")),
          options: [
            { text: capitalize(this.$t("all")), value: null },
            ...Object.entries(closeoutSnapshotStatusLabels).map(([value, i18nKey]) => ({
              text: capitalize(this.$t(i18nKey)),
              value,
            })),
          ],
        },
        performerCategory: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("category")),
          hidden: true,
          options: [
            { text: capitalize(this.$t("all")), value: null },
            { text: capitalize(performerCategoryTypes.FEATURED), value: "F" },
            { text: capitalize(performerCategoryTypes.NON_FEATURED), value: "N" },
          ],
        },
        ownershipConflict: {
          value: null,
          defaultValue: null,
          hidden: true,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_ownership_conflict")),
        },
        lineupConflict: {
          value: null,
          defaultValue: null,
          hidden: true,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_lineup_conflict_title")),
        },
        mandateConflict: {
          value: null,
          defaultValue: null,
          options: [
            { value: "1", text: capitalize(this.$t("no")) },
            { value: "0", text: capitalize(this.$t("yes")) },
          ],
          emptyLabel: capitalize(this.$t("all")),
          type: filterTypes.SELECT,
          label: capitalize(this.$t("no_mandate_conflict")),
        },
      },
      options: {
        sortable: [
          "recordingProprietaryId",
          "artist",
          "title",
          "rightsHolderProprietaryId",
          "name",
          "share",
          "paidPlaycount",
          "grossAmount",
          "eligibility",
          "paidAmount",
          "status",
          "lastUpdatedBy",
          "societyName",
          "payeeName",
          "performerCategory",
          "ownershipConflict",
          "lineupConflict",
          "mandateConflict",
          "preview",
        ],
        headings: {
          recordingProprietaryId: capitalize(this.$t("recording_id")),
          artist: capitalize(this.$t("artist")),
          title: capitalize(this.$t("title")),
          rightsHolderProprietaryId: capitalize(this.$t("rights_holder_id")),
          name: capitalize(this.$t("rights_holder_name")),
          share: capitalize(this.$t("percent_paid")),
          paidPlaycount: capitalize(this.$t("usage_paid")),
          payeeName: capitalize(this.$t("paid_payee")),
          societyName: capitalize(this.$t("paid_society")),
          performerCategory: capitalize(this.$t("category")),
          grossAmount: capitalize(this.$t("gross_amount")),
          eligibility: capitalize(this.$t("eligibility")),
          payeeNameCloseout: capitalize(this.$t("current_payee")),
          societyNameCloseout: capitalize(this.$t("current_society")),
          status: capitalize(this.$t("status")),
          lastUpdatedBy: capitalize(this.$t("last_updated_by")),
          comment: capitalize(this.$t("comment")),
          paidAmount: capitalize(this.$t("closeout_gross")),
          preview: capitalize(this.$t("preview")),
          ownershipConflict: this.$t("no_ownership_conflict"),
          lineupConflict: capitalize(this.$t("no_lineup_conflict_title")),
          mandateConflict: capitalize(this.$t("no_mandate_conflict")),
        },
        columnsClasses: {},
        requestFunction () {
          const queryParams = { ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))
          const rangeFields = ["gross_amount", "share", "paid_playcount", "paid_amount"]
          rangeFields.forEach(field => {
            if (queryParams[`${field}_min`]) {
              queryParams[`${field}__gte`] = queryParams[`${field}_min`]
              delete queryParams[`${field}_min`]
            }
            if (queryParams[`${field}_max`]) {
              queryParams[`${field}__lte`] = queryParams[`${field}_max`]
              delete queryParams[`${field}_max`]
            }
          })
          Object.keys(queryParams).forEach(key => {
            if (queryParams[key] === "" || queryParams[key] === null) delete queryParams[key]
          })
          return this.$api.distributions.getCloseoutSnapshotList(
            this.$parent.$parent.closeoutId,
            queryParams,
          )
        },
        responseAdapter ({ data }) {
          this.$parent.$parent.$options.parent.file = data.context
          return {
            data: data.results,
            count: data.count
          }
        },
      },
    }
  },
  computed: {
    ...mapState("consts", ["memberCollectives"]),
    columns () {
      if (!this.college) return []
      return [
        "recordingProprietaryId",
        "artist",
        "title",
        "rightsHolderProprietaryId",
        "name",
        "share",
        "paidPlaycount",
        "payeeName",
        "societyName",
        ...(this.college === distributionCollegeTypes.PERFORMER ? ["performerCategory"] : []),
        "grossAmount",
        "eligibility",
        "payeeNameCloseout",
        "societyNameCloseout",
        "status",
        "lastUpdatedBy",
        "comment",
        ...(this.college !== distributionCollegeTypes.PERFORMER ? ["ownershipConflict"] : []),
        ...(this.college !== distributionCollegeTypes.MAKER ? ["lineupConflict"] : []),
        "mandateConflict",
        "paidAmount",
        "preview",
      ]
    },
    pendingStatusMap () {
      return Object.fromEntries(this.pendingChanges.map(c => [c.snapshotId, c.status]))
    },
    pendingCommentMap () {
      return Object.fromEntries(
        this.pendingChanges
          .filter(c => c.comment !== undefined)
          .map(c => [c.snapshotId, c.comment])
      )
    },
    pendingRepresentationMap () {
      return Object.fromEntries(
        this.pendingChanges
          .filter(c => c.representation !== undefined)
          .map(c => [c.snapshotId, c.representation])
      )
    },
    actionStatusOptions () {
      return Object.entries(closeoutSnapshotStatusActionsLabels).map(([value, i18nKey]) => ({
        value,
        text: capitalize(this.$t(i18nKey)),
      }))
    },
  },
  watch: {
    "$store.state.consts.memberCollectives": function (value) {
      this.setSocietyOptions(value)
    },
    college: {
      immediate: true,
      handler (value) {
        this.filters.performerCategory.hidden = value !== distributionCollegeTypes.PERFORMER
        this.filters.ownershipConflict.hidden = value === distributionCollegeTypes.PERFORMER
        this.filters.lineupConflict.hidden = value === distributionCollegeTypes.MAKER
      },
    },
    selectedRows (value) {
      this.$emit("selection-change", value)
    },
    pendingChanges (value) {
      this.$emit("pending-changes", value)
    },
  },
  methods: {
    setSocietyOptions (collectives) {
      const options = collectives.map(x => ({ text: x.name, value: x.name }))
      this.filters.societyName.options = options
      this.filters.societyNameCloseout.options = options
    },
    getData () {
      this.$refs.reviewTable && this.$refs.reviewTable.getData()
    },
    onLoaded ({ data }) {
      this.count = data.count
      this.loading = false
    },
    onPagination (page) {
      this.page = page
      this.updateRouterPagination("page", page)
    },
    onLoading () {
      this.loading = true
    },
    onFilter (name, value) {
      if (name === "rightsHolderProprietaryId" || name === "recordingProprietaryId") {
        this.changeFilter(name, value === null ? null : value.resoundId)
      } else {
        this.changeFilter(name, value)
      }
    },
    onSorted (data) {
      this.queryParams = {
        ...this.queryParams,
        sorting: data.map(item => item.ascending ? snakeCase(item.column) : `-${snakeCase(item.column)}`),
      }
      this.applyFilters()
    },
    getStatusLabel (status) {
      const i18nKey = closeoutSnapshotStatusLabels[status]
      return i18nKey ? capitalize(this.$t(i18nKey)) : status
    },
    _applyChangesToRow (row, changes) {
      const existing = this.pendingChanges.findIndex(c => c.snapshotId === row.snapshotId)
      const entry = this._buildPendingEntry(row, { ...(existing >= 0 ? this.pendingChanges[existing] : {}), ...changes })
      if (existing >= 0) {
        this.pendingChanges.splice(existing, 1, entry)
      } else {
        this.pendingChanges.push(entry)
      }
    },
    _buildPendingEntry (row, changes) {
      return {
        snapshotId: row.snapshotId,
        recordingProprietaryId: row.recordingProprietaryId,
        rightsHolderProprietaryId: row.rightsHolderProprietaryId,
        payeeId: row.payeeId,
        ...changes,
      }
    },
    getRowStatusOptions (rowStatus) {
      const base = this.actionStatusOptions
      if (!rowStatus || base.some(o => o.value === rowStatus)) return base
      return [
        { value: rowStatus, text: this.getStatusLabel(rowStatus), disabled: true },
        ...base,
      ]
    },
    onRowActionChange (row, status) {
      if (!status) return
      const isSelected = this.selectedRows.some(r => r.snapshotId === row.snapshotId)
      const rowsToUpdate = isSelected ? this.selectedRows : [row]
      rowsToUpdate.forEach(targetRow => this._applyChangesToRow(targetRow, { status }))
    },
    openCommentModal (row) {
      this.editingCommentRow = row
      this.editingCommentValue = this.pendingCommentMap[row.snapshotId] !== undefined
        ? this.pendingCommentMap[row.snapshotId]
        : (row.comment || "")
      this.$bvModal.show("edit-comment-modal")
    },
    cancelCommentEdit () {
      this.$bvModal.hide("edit-comment-modal")
    },
    okCommentEdit () {
      this.$bvModal.hide("edit-comment-modal")
      this.confirmCommentEdit()
    },
    confirmCommentEdit () {
      const row = this.editingCommentRow
      const comment = this.editingCommentValue
      this.editingCommentRow = null
      const isSelected = this.selectedRows.some(r => r.snapshotId === row.snapshotId)
      const rowsToUpdate = isSelected ? this.selectedRows : [row]
      rowsToUpdate.forEach(targetRow => this._applyChangesToRow(targetRow, { comment }))
    },
    clearSelection () {
      this.selectedRows = []
    },
    getRepresentationSource (row) {
      if (this.pendingRepresentationMap[row.snapshotId]) {
        return this.pendingRepresentationMap[row.snapshotId]
      }
      return (row.payeesCloseout && row.payeesCloseout.length)
        ? row.payeesCloseout
        : (row.currentRepresentation || [])
    },
    getPrimaryRepresentative (row) {
      const source = this.getRepresentationSource(row)
      if (!source.length) return {}
      return source.reduce((best, item) =>
        Number(item.split) > Number(best.split) ? item : best
      )
    },
    openRepresentationModal (row) {
      this.representationModalItems = (row.payeesCloseout && row.payeesCloseout.length)
        ? row.payeesCloseout
        : (row.currentRepresentation || [])
      this.$refs.representationModal.show(row, this.pendingRepresentationMap[row.snapshotId])
    },
    onRepresentationConfirm ({ row, items }) {
      const isSelected = this.selectedRows.some(r => r.snapshotId === row.snapshotId)
      const rowsToUpdate = isSelected ? this.selectedRows : [row]
      rowsToUpdate.forEach(targetRow => this._applyChangesToRow(targetRow, { representation: items }))
    },
    getPerformerCategoryLabel (category) {
      if (!category) return ""
      const map = {
        F: capitalize(performerCategoryTypes.FEATURED),
        N: capitalize(performerCategoryTypes.NON_FEATURED),
      }
      return map[category] ?? category
    },
  },
  mounted () {
    this.setSocietyOptions(this.memberCollectives)
    this.updateTableSortIcons(this.$refs.reviewTable.$children[0], true)
  },
}
</script>
